/**
 * register-cta — directive `v-register-cta` posée sur TOUT appel à l'action qui
 * mène à l'inscription.
 *
 * POURQUOI ELLE EXISTE (mesuré en production le 14/09/2026) :
 *   le site compte 62 liens vers `/register` répartis sur 48 fichiers. 54
 *   n'émettaient RIEN du tout ; les 8 autres appelaient `trackSignupClick()`,
 *   qui ne parle qu'à Google Analytics. La table `lead_events` ne contenait
 *   donc AUCUN `cta_click` depuis la mise en service du traceur le 29/07 —
 *   impossible de savoir quel emplacement travaille et lequel est décoratif.
 *
 * POURQUOI UNE DIRECTIVE ET PAS UN COMPOSANT ENVELOPPANT :
 *   les 62 appels sont stylés différemment (variantes UButton, classes
 *   utilitaires, tailles). Les envelopper dans un composant commun aurait
 *   touché le balisage et le rendu de 48 fichiers pour un besoin de MESURE.
 *   Une directive n'ajoute qu'un attribut : zéro changement de balisage, zéro
 *   changement de style, donc zéro risque de régression visuelle.
 *
 * USAGE — le nom d'emplacement est OBLIGATOIRE, c'est lui qui porte la mesure :
 *   <NuxtLink :to="registerLink" v-register-cta="'hero'">…</NuxtLink>
 *   <UButton :to="registerLink" v-register-cta="'fin-article'">…</UButton>
 *
 * CE QU'ELLE ÉMET, ET CE QU'ELLE N'ÉMET PAS :
 *   · elle écrit `lead_events.cta_click` — first-party, non échantillonné,
 *     joignable aux inscriptions par `visitor_id`. C'est lui qui répond
 *     « quel bouton a amené cette association » ;
 *   · elle NE touche PAS à Google Analytics. Les 10 appels manuels à
 *     `trackSignupClick()` déjà en place gardent leurs libellés historiques
 *     ('header', 'cta_banner', 'footer'…) et continuent de fonctionner tels
 *     quels. Les faire passer par la directive aurait multiplié par huit le
 *     volume de l'événement `signup_click` — un indicateur qui existe depuis
 *     des mois — et rendu incomparables les rapports d'avant et d'après.
 *
 * Une responsabilité, une destination : aucun double comptage possible.
 *
 * RGPD : on n'envoie que l'emplacement, le chemin de la page et le libellé
 * visible du bouton. Aucune donnée personnelle, conformément à l'en-tête de la
 * migration 149.
 */
import type { DirectiveBinding } from "vue";

/** Emplacements connus — étiquettes lisibles dans le tableau de bord Leads. */
export type RegisterCtaPlacement =
  | "hero"
  | "nav"
  | "bandeau-final"
  | "milieu-article"
  | "fin-article"
  | "tarifs"
  | "fonctionnalites"
  | "comparateur"
  | "pied-de-page"
  | (string & {});

/** Handler posé sur l'élément, conservé pour pouvoir le retirer au démontage. */
const HANDLERS = new WeakMap<HTMLElement, EventListener>();

/** Libellé visible du bouton, borné — sert à distinguer deux CTA d'une même page. */
function libelleDe(el: HTMLElement): string | undefined {
  const texte = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  return texte ? texte.slice(0, 80) : undefined;
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive("register-cta", {
    /**
     * Rendu serveur : la directive n'ajoute aucun attribut au HTML. Elle est
     * déclarée dans un plugin UNIVERSEL (et non `.client`) pour que Vue sache
     * la résoudre pendant le SSR — sinon chaque page rendue côté serveur
     * émettrait « Failed to resolve directive: register-cta ».
     */
    getSSRProps: () => ({}),

    mounted(el: HTMLElement, binding: DirectiveBinding<RegisterCtaPlacement>) {
      const placement = binding.value;
      if (!placement) {
        // Un emplacement anonyme ne mesure rien : on le signale en dev plutôt
        // que d'envoyer un événement inexploitable.
        if (import.meta.dev) {
          console.warn("[register-cta] emplacement manquant sur", el);
        }
        return;
      }

      const onClick: EventListener = () => {
        try {
          const track = nuxtApp.$leadTrack as
            | ((eventType: string, meta?: Record<string, unknown>) => void)
            | undefined;
          track?.("cta_click", {
            placement,
            path: window.location.pathname,
            label: libelleDe(el),
          });
        } catch {
          /* best-effort : une télémétrie en échec ne doit jamais gêner le clic */
        }
      };

      // Phase de capture : l'événement est relevé même si la navigation du
      // NuxtLink arrête la propagation avant d'atteindre la phase de bouillonnement.
      el.addEventListener("click", onClick, { capture: true });
      HANDLERS.set(el, onClick);
    },

    beforeUnmount(el: HTMLElement) {
      const onClick = HANDLERS.get(el);
      if (onClick) {
        el.removeEventListener("click", onClick, { capture: true });
        HANDLERS.delete(el);
      }
    },
  });
});
